// 日別「シナリオ実行時刻」タイムライン用のデータ整形。
// scenario-stats API の daily[].runs (日次集計済みの実行区間 [開始分, 終了分, scenario index, flag])
// を描画用の構造にし、稼働時間 (初回開始〜最終終了) と実行時間合計を日別に出す。
// 稼働時間 × 日数から投資対効果 (人の拘束時間との比較) を見積もる用途。

import type {
  ScenarioDailyStats,
  ScenarioDetail,
  ScenarioRunInterval,
} from '@/types/forklift'

// flag の意味 (Lambda 側 RUN_FLAG と同じ)
export const RUN_FLAG = {
  completed: 0,
  canceled: 1,
  errorStopped: 2,
  open: 3,
} as const

export interface TimelineSegment {
  scenarioId: string
  // 当日 0:00 (JST) からの分
  startMin: number
  endMin: number
  flag: number
  // 終了未観測 (日跨ぎ・実行中・終了ログ欠損)。稼働時間の計算からは除外
  open: boolean
  // 複数機体合算時の機体の並び順 (単体は 0)。帯を機体ごとに横に分けて描く
  lane: number
  // 帯に含まれる実行時間 (分) と実行回数。単体は帯の長さと 1、合算時は 1 日分の合計
  runMin: number
  count: number
}

export interface DayTimeline {
  date: string // yyyy-MM-dd (JST)
  segments: TimelineSegment[]
  // 終了が確定した実行のみで計算 (open な帯は除く)。実行なしの日は null
  firstStartMin: number | null
  lastEndMin: number | null
  // 稼働時間 = 初回開始〜最終終了 (分)
  boundMin: number
  // 実行時間合計 = 各帯の長さの合計 (分)
  runMin: number
  runCount: number
}

export interface DailyTimeline {
  days: DayTimeline[]
  // 表示する時刻レンジ (時, 0〜24)。データに合わせて切り上げ/切り下げ
  hourMin: number
  hourMax: number
  // 帯を横に分ける本数 (複数機体合算時は機体数、単体は 1)
  laneCount: number
  // サマリー: 実行のあった日数と 1 稼働日あたりの平均 (分)
  activeDays: number
  avgBoundMin: number
  avgRunMin: number
}

export function formatMinutes(min: number): string {
  const total = Math.round(min)
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

export function formatMinutesDuration(min: number): string {
  const total = Math.round(min)
  if (total <= 0) return '0分'
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h > 0) return `${h}時間${m}分`
  return `${m}分`
}

const MIN_HOUR_SPAN = 6

function toSegment(
  run: ScenarioRunInterval,
  scenarios: ScenarioDetail[]
): TimelineSegment | null {
  const [startMin, endMin, index, flag, lane, runMin, count] = run
  if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return null
  const scenario = index >= 0 ? scenarios[index] : undefined
  const s = Math.max(0, Math.min(1440, startMin))
  const e = Math.max(0, Math.min(1440, Math.max(startMin, endMin)))
  return {
    scenarioId: scenario?.id ?? '',
    startMin: s,
    endMin: e,
    flag,
    open: flag === RUN_FLAG.open,
    lane: lane ?? 0,
    runMin: runMin ?? e - s,
    count: count ?? 1,
  }
}

export function buildDailyTimeline(
  daily: ScenarioDailyStats[],
  scenarios: ScenarioDetail[]
): DailyTimeline {
  let minStart = Infinity
  let maxEnd = -Infinity
  let activeDays = 0
  let boundSum = 0
  let runSum = 0
  let maxLane = 0

  const days: DayTimeline[] = daily.map((d) => {
    const day: DayTimeline = {
      date: d.date,
      segments: [],
      firstStartMin: null,
      lastEndMin: null,
      boundMin: 0,
      runMin: 0,
      runCount: 0,
    }
    for (const run of d.runs ?? []) {
      const seg = toSegment(run, scenarios)
      if (!seg) continue
      day.segments.push(seg)
      maxLane = Math.max(maxLane, seg.lane)
    }
    day.segments.sort((a, b) => a.startMin - b.startMin)
    day.runCount = day.segments.reduce((n, seg) => n + seg.count, 0)
    for (const seg of day.segments) {
      minStart = Math.min(minStart, seg.startMin)
      maxEnd = Math.max(maxEnd, seg.endMin)
      if (seg.open) continue
      day.firstStartMin =
        day.firstStartMin == null
          ? seg.startMin
          : Math.min(day.firstStartMin, seg.startMin)
      day.lastEndMin =
        day.lastEndMin == null
          ? seg.endMin
          : Math.max(day.lastEndMin, seg.endMin)
      day.runMin += seg.runMin
    }
    if (day.firstStartMin != null && day.lastEndMin != null) {
      day.boundMin = day.lastEndMin - day.firstStartMin
      activeDays += 1
      boundSum += day.boundMin
      runSum += day.runMin
    }
    return day
  })

  // 時刻レンジ: データの前後を時間単位に丸め、最低 MIN_HOUR_SPAN 時間は確保
  let hourMin: number
  let hourMax: number
  if (minStart === Infinity) {
    hourMin = 8
    hourMax = 18
  } else {
    hourMin = Math.max(0, Math.floor(minStart / 60))
    hourMax = Math.min(24, Math.ceil(maxEnd / 60))
    if (hourMax - hourMin < MIN_HOUR_SPAN) {
      hourMax = Math.min(24, hourMin + MIN_HOUR_SPAN)
      hourMin = Math.max(0, hourMax - MIN_HOUR_SPAN)
    }
  }

  return {
    days,
    hourMin,
    hourMax,
    laneCount: maxLane + 1,
    activeDays,
    avgBoundMin: activeDays > 0 ? boundSum / activeDays : 0,
    avgRunMin: activeDays > 0 ? runSum / activeDays : 0,
  }
}
